import React from "react";
import { useSelector } from "react-redux";
import BuildingIcon from "@react-spectrum/s2/icons/Building";
import UserIcon from "@react-spectrum/s2/icons/User";
import UserGroupIcon from "@react-spectrum/s2/icons/UserGroup";
import EnterpriseIcon from "@react-spectrum/s2/icons/Enterprise";
import WalletIcon from "@react-spectrum/s2/icons/Wallet";
import CheckmarkIcon from "@react-spectrum/s2/icons/Checkmark";
import { asS2Icon } from "./Common/spectrumCompat";
import { getSupportCaseLink } from "../Helpers/utils";
import { getWizardNavSteps } from "./registrationNavSteps";
import type { LeftNavigationPreviewProps } from "./LeftNavigation";
import "./css/registration.css";

const Building = asS2Icon(BuildingIcon);
const User = asS2Icon(UserIcon);
const UserGroup = asS2Icon(UserGroupIcon);
const Enterprise = asS2Icon(EnterpriseIcon);
const Wallet = asS2Icon(WalletIcon);
const Checkmark = asS2Icon(CheckmarkIcon);

const STEP_ICONS = [Building, User, UserGroup, Enterprise, Wallet] as const;

type AppState = {
  registration?: {
    partnerRegistrationFormStep?: number;
    selectedRegisterCompanyType?: string | null;
  };
};

function HorizontalRegistrationStepper({
  previewStepOverride,
  previewCompanyTypeOverride,
}: LeftNavigationPreviewProps) {
  const registrationState = useSelector(
    (state: AppState) => state.registration,
  );
  const currentStep =
    previewStepOverride !== undefined
      ? previewStepOverride
      : registrationState?.partnerRegistrationFormStep || 0;
  const selectedRegisterCompanyType =
    previewCompanyTypeOverride !== undefined
      ? previewCompanyTypeOverride
      : (registrationState?.selectedRegisterCompanyType ??
        (typeof localStorage !== "undefined"
          ? localStorage.getItem("selectedRegisterCompanyType")
          : null));

  if (currentStep === 0) {
    return null;
  }

  const steps = getWizardNavSteps(selectedRegisterCompanyType);

  return (
    <nav className="dxp-horizontal-stepper" aria-label="Registration progress">
      <div className="dxp-horizontal-stepper__program">
        ADOBE DIGITAL EXPERIENCE PARTNER PROGRAM
      </div>
      <div className="dxp-horizontal-stepper__heading">Create account</div>

      <div className="dxp-horizontal-stepper__row">
        {steps.map((step, index) => {
          const isComplete = step.key < currentStep;
          const isCurrent = step.key === currentStep;
          const isUpcoming = step.key > currentStep;
          const Icon = STEP_ICONS[index];
          const colClass = [
            "dxp-horizontal-stepper__col",
            isUpcoming ? "dxp-horizontal-stepper__col--upcoming" : "",
            isCurrent ? "dxp-horizontal-stepper__col--current" : "",
          ]
            .filter(Boolean)
            .join(" ");

          const iconBoxClass = [
            "dxp-horizontal-stepper__iconBox",
            isComplete ? "dxp-horizontal-stepper__iconBox--completed" : "",
            isCurrent ? "dxp-horizontal-stepper__iconBox--current" : "",
            isUpcoming ? "dxp-horizontal-stepper__iconBox--upcoming" : "",
          ]
            .filter(Boolean)
            .join(" ");

          let stepIcon: React.ReactNode = null;
          if (isComplete) {
            stepIcon = <Checkmark />;
          } else if (Icon) {
            stepIcon = <Icon />;
          }

          return (
            <div key={step.key} className={colClass}>
              <div className="dxp-horizontal-stepper__top">
                {index > 0 ? (
                  <div
                    className={[
                      "dxp-horizontal-stepper__seg",
                      currentStep >= step.key
                        ? "dxp-horizontal-stepper__seg--done"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-hidden
                  />
                ) : (
                  <div className="dxp-horizontal-stepper__segSpacer" />
                )}
                <div className={iconBoxClass}>{stepIcon}</div>
                {index < steps.length - 1 ? (
                  <div
                    className={[
                      "dxp-horizontal-stepper__seg",
                      currentStep > step.key
                        ? "dxp-horizontal-stepper__seg--done"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    aria-hidden
                  />
                ) : (
                  <div className="dxp-horizontal-stepper__segSpacer" />
                )}
              </div>
              <span className="dxp-horizontal-stepper__label">
                {step.label}
              </span>
              {/* <span className="dxp-horizontal-stepper__sub">
                {step.subtitle}
              </span> */}
            </div>
          );
        })}
      </div>

      <div className="dxp-horizontal-stepper__support">
        <span className="dxp-horizontal-stepper__supportText">
          Questions?{" "}
          <button
            type="button"
            className="dxp-horizontal-stepper__supportLink"
            onClick={() => {
              window.open(getSupportCaseLink(), "_blank");
            }}
          >
            Contact our support team
          </button>
        </span>
      </div>
    </nav>
  );
}

HorizontalRegistrationStepper.defaultProps = {
  previewStepOverride: undefined,
  previewCompanyTypeOverride: undefined,
};

export default HorizontalRegistrationStepper;
